import { EventCacheData } from 'hooks/useLRQ/useEventCache';
import { LRQRequest } from 'hooks/useLRQ/useLRQ';
import * as K from 'common/constants';
import { SortOrder } from 'common/graphqlTypes';

/**
 * The anchor results might include either or both of an ascending and descending result. For the ascending result, the
 * token of the first match is the anchor cursor, and for the descending result, the token of the last match is the
 * anchor cursor. This function returns a truthy anchor cursor from the descending result if possible. Otherwise it
 * returns a truthy anchor cursor from the ascending result, or an empty string. There is no significance to the fact
 * that the anchor cursor from the descending result wins ties.
 *
 * @param anchorResults         results from the event cache for the current anchor requests
 *
 * @returns The anchor cursor for these results
 */
export const findAnchorCursor = (anchorResults: EventCacheData[]): string => {

  const ascendingResult = anchorResults.find((result) => result.queryVariables.ascending);
  const descendingResult = anchorResults.find((result) => !result.queryVariables.ascending);
  const ascendingAnchor = ascendingResult?.logEntryData.matches?.[0]?.continuationToken || '';
  const descendingAnchor = descendingResult?.logEntryData.matches?.slice(-1)[0]?.continuationToken || '';
  return descendingAnchor || ascendingAnchor;
};

/**
 * Builds the list of paginated requests from the list of anchor requests along with the responses to the anchor
 * requests. The match counts returned in each response, along with the totalMatchCount are used to help construct the
 * model for paginated requests. The list of paginated requests represents all of the blocks of data that will
 * eventually be fetched with continuation requests to fill out all of the data.
 *
 * @param anchorRequests        list of anchor requests
 * @param anchorResults         list of the results of the anchor requests
 * @param totalMatchCount       a reliable total match count for this search, which must come from a PLOT request.
 * @param sortOrder             the sort order that will be used to display the events
 *
 * @returns The list of paginated requests
 */
export const initializePaginatedRequests = (
  anchorRequests: LRQRequest[],
  anchorResults: EventCacheData[],
  totalMatchCount = 0,
  sortOrder = K.DEFAULT_EVENT_SORT,
): LRQRequest[] => {

  let newPaginatedRequests = [ ...anchorRequests ];
  let rightPadBlocks = 0;
  let leftPadBlocks = 0;
  const ascendingResult = anchorResults.find((result) => result.queryVariables.ascending);
  const descendingResult = anchorResults.find((result) => !result.queryVariables.ascending);

  // The match counts returned by LOG requests are not reliable, but they are still useful to construct a lower bound,
  // since they do accurately convey that there are at least this many entries in this direction.
  // Fixme: eventually need to remove this final dependency on unreliable LOG query matchCount
  const ascendingMatchCountLowerBound: number = ascendingResult?.logEntryData.matchCount || 0;
  const descendingMatchCountLowerBound: number = descendingResult?.logEntryData.matchCount || 0;

  // The lower bound for a direction is considered to be reliable if the server indicates that there are no more entries
  // in that direction (this can be inferred on the client when the number of matches returned is fewer than the number
  // requested by the client), or if there is no result in that direction at all. In the latter case, 0 is returned for
  // the lower bound, and it is accurate.
  const isAscendingLowerBoundReliable: boolean = !ascendingResult || ascendingResult.logEntryData.matches?.length < ascendingResult.queryVariables.numResults;
  const isDescendingLowerBoundReliable: boolean = !descendingResult || descendingResult.logEntryData.matches?.length < descendingResult.queryVariables.numResults;

  // If the lower bound in a direction is not reliable, we construct the upper bound by subtracting the lower bound for
  // the other direction from the reliable total match count.
  const ascendingMatchCountUpperBound: number = isAscendingLowerBoundReliable ? ascendingMatchCountLowerBound : totalMatchCount - descendingMatchCountLowerBound;
  const descendingMatchCountUpperBound: number = isDescendingLowerBoundReliable ? descendingMatchCountLowerBound : totalMatchCount - ascendingMatchCountLowerBound;

  // Potential future ascending queries, which are about traversing from older events to newer, may correspond to either
  // the left or right side of pagination model as represented by newPaginatedRequests, depending on sort order.
  const matchCountRight = sortOrder === SortOrder.MOST_RECENT_AT_BOTTOM ? ascendingMatchCountUpperBound : descendingMatchCountUpperBound;
  const matchCountLeft = sortOrder === SortOrder.MOST_RECENT_AT_BOTTOM ? descendingMatchCountUpperBound : ascendingMatchCountUpperBound;

  // Calculate the number of additional blocks that might be required in a direction from the upper bound. It is ok if a
  // block is reserved for a request unnecessarily, but we want to make sure that a request that does need a block
  // reserved always has one.
  let availableBlocksRight = Math.ceil(matchCountRight / 200);
  availableBlocksRight = availableBlocksRight > 0 ? availableBlocksRight - 1 : 0; // Don't include the block for the anchor request itself
  let availableBlocksLeft = Math.ceil(matchCountLeft / 200);
  availableBlocksLeft = availableBlocksLeft > 0 ? availableBlocksLeft - 1 : 0; // Don't include the block for the anchor request itself

  if (matchCountRight < matchCountLeft) {
    // we want to fill the short side first, so that we know how much room to make on the long side, so in this case,
    // find the number of empty blocks to pad the right before making the calculation on the left side
    rightPadBlocks = Math.min(4, availableBlocksRight);
    leftPadBlocks = Math.min(10 - newPaginatedRequests.length - rightPadBlocks, availableBlocksLeft);
  } else {
    // find number of empty blocks to pad the left side first
    leftPadBlocks = Math.min(4, availableBlocksLeft);
    rightPadBlocks = Math.min(10 - newPaginatedRequests.length - leftPadBlocks, availableBlocksRight);
  }

  if (leftPadBlocks > 0) {
    newPaginatedRequests = new Array(leftPadBlocks).concat(newPaginatedRequests);
  }
  if (rightPadBlocks > 0) {
    newPaginatedRequests = newPaginatedRequests.concat(new Array(rightPadBlocks));
  }
  return newPaginatedRequests;
};
